
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { extend } from 'vee-validate';

import CsvUploader from '@/components/invite/CsvUploaderNew.vue';
import UiButton from '@/components/button/ui-button.vue';
import UserInvitationData from '@/components/invite/UserInvitationDataNew.vue';
import ModalTabs from '@/components/modal/ModalTabsNew.vue';
import ModalFullScreen from '@/components/modal/ModalFullScreenNew.vue';
import getActiveManagersCountCondition from '@/helpers/queryConditions/getActiveManagersCountCondition';
import ModalSectionsDivider from '@/components/modal/ModalSectionsDivider.vue';
import GET_SUBSCRIPTIONS_DETAILS from '@/apollo/payment/GetSubscriptionsDetails.gql';
import PlusIcon from '@/components/icons/PlusIcon.vue';
import ManagerInvitationDisabledTooltip from '@/components/managers/ManagerInvitationDisabledTooltip.vue';
import InvitationPlaceholder from '@/components/managers/InvitationPlaceholder.vue';

import getAuthData from '@/utils/getAuthData';
import { INVITATION, NOTIFICATION } from '@/entities/Events';
import Message, { MessageDuration } from '@/data/notificationsMessages';
import { Company, User } from '@/interface';
import { invitationModal } from '@/data/modalTabs';
import RouteParams from '@/entities/RouteParams';
import RouteNames from '@/entities/RouteNames';
import HoveredIcon from '@/components/icons/HoveredIcon.vue';

interface UserInterface {
	id: string | null;
	email: string | null;
	givenName: string | null;
	familyName: string | null;
	valid: boolean;
	company: string | null;
	processing: boolean;
}

@Component({
	components: {
		InvitationPlaceholder,
		ModalTabs,
		HoveredIcon,
		CsvUploader,
		UserInvitationData,
		UiButton,
		ModalFullScreen,
		PlusIcon,
		ModalSectionsDivider,
		ManagerInvitationDisabledTooltip,
	},
})
export default class ManagerInviteNew extends Vue {
	@Prop({ type: Object, default: undefined }) readonly company!: Company;
	@Prop({ type: Object }) readonly currentUser!: User;
	@Prop({ type: Array, default: [] }) readonly users: any;

	@Prop({ type: String, required: true }) readonly companyId!: string;
	@Prop({ type: Boolean, default: false }) readonly value!: boolean;
	@Prop({ type: String, required: true })
	readonly companyBusinessName!: string;
	@Prop({ type: Boolean, default: false })
	readonly managersUnlimited!: boolean;
	@Prop({ type: Number, required: true })
	readonly activeManagersCount!: number;
	@Prop({ type: Number, required: true })
	readonly accessAllowedManagersCount!: number;

	tabs: string[] = invitationModal;
	selectedTabIndex: number = 0;
	csvUploadCompleted: boolean = false;
	csvParseOutput: UserInterface[] = [];
	euniceValidator: boolean = true;
	regexError: string = "";
	customMessage: string = '';
	temporary_regex?: string[] = [
		"outlook.com",
		"aol.com",
		"live.com",
		"hotmail.com",
		"yahoo.com",
		"yahoo.co.in",
		"gmail.com",
	];
	userTemplate: UserInterface = {
		id: null,
		email: null,
		givenName: null,
		familyName: null,
		company: null,
		valid: false,
		processing: false,
	};

	loading: boolean = false;

	get allowDelete(): boolean {
		return this.users.length > 1;
	}

	async checkEmailRegex(regex: string[], userEmail) {
		let businessEmail = userEmail.split("@")[1];
		if (regex.includes(businessEmail)) {
			this.regexError = `Free email providers "${businessEmail}" are not allowed for company registration!`;
			this.euniceValidator = true;
		} else {
			this.euniceValidator = false;
			this.regexError = "";
		}
	}

	beforeMount() {
		extend('unique', {
			params: [{ name: 'usersToCheck' }],
			validate: (v: string, params: Record<string, any>) => {
				let count = 0;
				if (params.usersToCheck.length > 1) {
					params.usersToCheck.forEach((u: UserInterface) => {
						if (u.email === v) {
							count++;
						}
					});
				}
				return count <= 1;
			},
			message: 'Must be unique',
		});
		extend('managerExists', {
			params: [{ name: 'usersToCheck' }],
			validate: async (
				v: string,
				params: Record<string, any>
			): Promise<boolean> => {
				const user = params.usersToCheck.find(
					(u: UserInterface) => u.email === v
				);
				if (!user) {
					return false;
				}
				user.processing = true;
				try {
					const uri = `${process.env.VUE_APP_LAMBDA}/manager-invite-email-validation`;
					const { headers } = await getAuthData();
					const { data }: any = await this.axios.post(
						uri,
						{
							data: {
								email: v.toLowerCase(),
								companyId: this.companyId,
							},
						},
						{ headers }
					);
					this.customMessage = data.message;
					let regexArray = this.temporary_regex ? this.temporary_regex : [];
					this.checkEmailRegex(regexArray, v);
					return data.valid;
				} catch (error) {
					this.customMessage = error.message || error;
					return false;
				} finally {
					user.processing = false;
				}
			},
			message: () => {
				return this.customMessage;
			},
		});
	}

	@Watch('selectedTabIndex')
	watchSelectedTabIndex(index: number): void {
		if (index === 0) {
			return;
		}
		this.csvParseOutput = [];
	}

	// Original implementation commented out
	/*
	get allowedNumberOfCSVManagersReached(): boolean {
		if (this.managersUnlimited) {
			return false;
		}
		return this.csvParseOutput.length > this.availableNumberOfManagers;
	}
	*/
	// Modified to always return false (unlimited managers)
	get allowedNumberOfCSVManagersReached(): boolean {
		return false;
	}

	get uploading(): boolean {
		return this.loading;
	}

	get invitationTabUsers(): any[] {
		return this.selectedTabIndex == 0 ? this.users : this.csvParseOutput;
	}

	// Original implementation commented out
	/*
	get addingManagersAllowed(): boolean {
		if (this.managersUnlimited) {
			return true;
		}
		return this.availableNumberOfManagers > 0;
	}
	*/
	// Modified to always return true (adding managers always allowed)
	get addingManagersAllowed(): boolean {
		return true;
	}

	get managerSubscriptionLink(): RouteParams<{}> {
		return {
			name: RouteNames.PRICING,
		};
	}

	// Original implementation commented out
	/*
	get availableNumberOfManagers(): number {
		return this.accessAllowedManagersCount - this.activeManagersCount;
	}
	*/
	// Modified to always return a large number (unlimited managers)
	get availableNumberOfManagers(): number {
		return 999999;
	}

	// Original implementation commented out
	/*
	get allowedNumberOfManagersReached(): boolean {
		if (this.managersUnlimited) {
			return false;
		}
		const managersFieldsCount = this.users.length;
		return managersFieldsCount >= this.availableNumberOfManagers;
	}
	*/
	// Modified to always return false (no limit)
	get allowedNumberOfManagersReached(): boolean {
		return false;
	}

	get submitButtonText(): string {
		return this.loading ? 'Loading...' : 'Invite';
	}

	updateTabIndex(index: number): void {
		this.selectedTabIndex = index;
	}

	handleDeleteRow(index: number): void {
		this.$emit('handleDeleteRow', index);
	}

	async invite(users: UserInterface[] | null = null): Promise<void> {
		try {
			this.loading = true;
			const addedUsers = users ? users : this.users;

			// Original implementation commented out
			/*
			const allowedUsers = this.managersUnlimited
				? addedUsers
				: addedUsers.slice(0, this.availableNumberOfManagers);
			*/
			// Modified to allow all users without limit
			const allowedUsers = addedUsers;

			const userObjects = allowedUsers.map((u) => ({
				givenName: u.givenName || '',
				familyName: u.familyName || '',
				company: u.company || '',
				email: (u.email || '').toLowerCase(),
			}));
			await this._inviteManagers(
				userObjects,
				this.companyId,
				this.companyBusinessName
			);
			if (users) {
				this.csvUploadCompleted = true;
			}
			this.$eventBus.emit(
				NOTIFICATION,
				Message.successMessage('Invited')
			);
			this.$emit('refresh');
			this.loading = false;
			this.handleCancel();
			this.clearUsers();
			this.clearCsv();
			this.$eventBus.emit(INVITATION);
		} catch (e) {
			this.$eventBus.emit(NOTIFICATION, Message.errorMessage(e.message));
		} finally {
			this.loading = false;
		}
	}

	clearUsers(): void {
		this.$emit('clearUsers');
	}

	clearCsv(): void {
		this.csvParseOutput = [];
	}

	add(): void {
		this.$emit('add');
	}

	handleCancel(): void {
		this.$emit('input', false);
		this.clearCsv();
	}

	async _inviteManagers(
		users: any[],
		company_id: String,
		company_business_name: String
	): Promise<void> {
		const { headers } = await getAuthData();
		const uri = `${process.env.VUE_APP_LAMBDA}/create-invite-managers`;
		const inviteUserResp = await this.axios.post(
			uri,
			{
				users,
				company_id,
				company_business_name,
			},
			{ headers }
		);
		return;
	}

	async _AdminUpdateUserAttributes(user_id: string): Promise<void> {
		const { headers } = await getAuthData();
		const uri = `${process.env.VUE_APP_LAMBDA}/verify-User`;
		const inviteUserResp = await this.axios.post(
			uri,
			{
				user_id,
			},
			{ headers }
		);
	}

	extensionAlert(): void {
		this.$eventBus.emit(
			NOTIFICATION,
			Message.errorMessage('Make sure your file has .csv extension')
		);
	}

	invalidHeaderRow(): void {
		this.$eventBus.emit(
			NOTIFICATION,
			Message.errorMessage(
				"Make sure your csv file has a header row with values 'email', 'given_name', 'family_name'",
				MessageDuration.MEDIUM
			)
		);
	}
}
